import React, { useRef, useState, useEffect } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Search } from 'lucide-react';
import PhoneFrame from '../PhoneFrame';
import Chips from '../Chips';
import { useTrioSizes } from '../../lib/useTrioSizes';

/**
 * 07 · GUIDE — both sides of the marketplace.
 * Find one: browse and vet as a swapping duo — the trainer list and the
 * coach profile trade the front seat every few seconds.
 * Be one: the coach's world as a trio — command center flanked by
 * revenue and Studio, same staging as the Daily Signals shelf.
 */
const SwappingDuo = ({ frontW }) => {
  const reduce = useReducedMotion();
  const duoRef = useRef(null);
  // Swap only while on screen
  const isOnScreen = useInView(duoRef, { margin: '100px' });
  // false → profile in front, true → trainer list in front
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    if (reduce || !isOnScreen) return undefined;
    const id = setInterval(() => setFlipped((f) => !f), 4200);
    return () => clearInterval(id);
  }, [reduce, isOnScreen]);

  const offset = Math.round(frontW * 0.56);
  const slots = {
    front: { x: offset, y: 0, scale: 1, rotateY: 0, opacity: 1 },
    back: { x: 0, y: 14, scale: 0.78, rotateY: 14, opacity: 0.92 },
  };
  const spring = { type: 'spring', stiffness: 80, damping: 17 };

  const phones = [
    {
      src: '/screens/coach-profile.webp',
      alt: 'Coach profile — Prajyot Rote, fat loss coach for busy professionals, NASM-CPT certified, skills and expertise',
      glow: 'rgba(147,51,234,0.28)',
      isFront: !flipped,
    },
    {
      src: '/screens/trainer-search.webp',
      alt: 'Find a Trainer — browse real coach profiles, filter by strength, fat loss, yoga or HIIT',
      glow: 'rgba(147,51,234,0.16)',
      isFront: flipped,
    },
  ];

  return (
    <div
      ref={duoRef}
      className="relative"
      style={{
        width: frontW + offset,
        height: Math.round(frontW * 2.24),
        perspective: 1600,
      }}
    >
      {phones.map((p) => (
        <motion.div
          key={p.src}
          className="absolute bottom-0 left-0"
          style={{ zIndex: p.isFront ? 20 : 10, transformOrigin: 'bottom center' }}
          initial={false}
          animate={reduce ? (p.isFront ? { ...slots.front, x: offset } : slots.back) : (p.isFront ? slots.front : slots.back)}
          transition={spring}
        >
          <PhoneFrame src={p.src} alt={p.alt} width={frontW} glowColor={p.glow} />
        </motion.div>
      ))}
    </div>
  );
};

const ProfessionalsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });
  const sizes = useTrioSizes();

  const rise = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  const inView = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-90px' },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="professionals"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg-alt overflow-hidden"
      data-testid="professionals-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.08), rgba(147,51,234,0.040) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-[720px] mx-auto">
          <motion.span className="section-label inline-block" {...rise(0)}>
            07 · GUIDE
          </motion.span>
          <motion.h2
            className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
            style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', letterSpacing: '-0.025em' }}
            {...rise(0.1)}
          >
            <span className="block text-ston-text">Find one.</span>
            <span className="block gradient-text">Or be one.</span>
          </motion.h2>
          <motion.p
            className="mt-5 text-[16.5px] text-ston-text-secondary leading-relaxed"
            {...rise(0.2)}
          >
            The best coaches, nutritionists and dietitians — handpicked for you,
            and empowered to do their best work.
          </motion.p>
        </div>

        {/* ---- Find one: single screen ---- */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <div>
            <motion.div className="flex items-center gap-3" {...rise(0.25)}>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ston-purple/15">
                <Search size={18} className="text-ston-purple-light" />
              </span>
              <span className="text-[11px] font-medium uppercase tracking-[2px] text-ston-purple-light">
                For you
              </span>
            </motion.div>
            <motion.h3
              className="mt-5 font-outfit font-extrabold italic text-ston-text leading-[1.06]"
              style={{ fontSize: 'clamp(26px, 3vw, 40px)', letterSpacing: '-0.02em' }}
              {...rise(0.32)}
            >
              Handpicked. Vetted. Proven.
            </motion.h3>
            <motion.p
              className="mt-2 text-[14px] font-medium text-ston-text-muted italic"
              {...rise(0.38)}
            >
              Don't know how to find professional help? That's exactly why we exist.
            </motion.p>
            <motion.p
              className="mt-4 text-[16px] text-ston-text-secondary leading-relaxed max-w-[48ch]"
              {...rise(0.44)}
            >
              Finding the right coach shouldn't be a gamble. Every coach,
              nutritionist and dietitian on STON.FIT is handpicked — real
              credentials, real client results, not just followers. Browse by
              specialty, goal and city, and choose the best person to work with —
              someone you'd actually trust with your health.
            </motion.p>
            <Chips items={['Handpicked coaches', 'Real results', 'Every specialty', 'You choose']} />
          </div>

          {/* Browse ⇄ vet: the trainer list and the profile swap the front seat */}
          <motion.div
            className="flex items-end justify-center lg:justify-end"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <SwappingDuo frontW={Math.round(sizes.center * 0.92)} />
          </motion.div>
        </div>

        {/* ---- Be one: the coach trio ---- */}
        <div className="mt-28 md:mt-36 text-center max-w-[780px] mx-auto">
          <motion.span
            className="text-[11px] font-medium uppercase tracking-[2px] text-ston-purple-light"
            {...inView(0)}
          >
            If you're the coach
          </motion.span>
          <motion.h3
            className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
            style={{ fontSize: 'clamp(28px, 3.8vw, 50px)', letterSpacing: '-0.025em' }}
            {...inView(0.1)}
          >
            <span className="block text-ston-text">Coaches shouldn't</span>
            <span className="block gradient-text">live on WhatsApp.</span>
          </motion.h3>
          <motion.p
            className="mt-6 mx-auto max-w-[60ch] text-[16.5px] md:text-[17.5px] text-ston-text-secondary leading-relaxed"
            {...inView(0.2)}
          >
            Clients, cash flow, systems — a command center that knows who needs you
            first, shows you the ₹30,000 that renews next month, and turns your
            methods into plans you build once and assign forever. WhatsApp can go
            back to being for family.
          </motion.p>
        </div>

        {/* Trio — the command center rises first, revenue and Studio fan out behind it */}
        <div
          className="mt-14 md:mt-16 flex items-end justify-center"
          style={{ perspective: 1600 }}
        >
          <motion.div
            className="opacity-90"
            initial={{ opacity: 0, x: sizes.center * 0.5, scale: 0.92 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ transform: 'rotateY(16deg) translateY(24px)' }}>
              <PhoneFrame
                src="/screens/coach-business-hub.webp"
                alt="Business hub — thirty thousand rupees monthly recurring revenue, up 92 percent"
                width={sizes.side}
                glowColor="rgba(74,222,128,0.18)"
              />
            </div>
          </motion.div>
          <motion.div
            className="relative z-10 -mx-6 md:-mx-8"
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <PhoneFrame
              src="/screens/coach-home.webp"
              alt="Coach command center — four clients waiting, action queue, pending check-ins"
              width={sizes.center}
              glowColor="rgba(147,51,234,0.28)"
            />
          </motion.div>
          <motion.div
            className="opacity-90"
            initial={{ opacity: 0, x: -sizes.center * 0.5, scale: 0.92 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ transform: 'rotateY(-16deg) translateY(24px)' }}>
              <PhoneFrame
                src="/screens/coach-studio.webp"
                alt="Studio — AI workout and diet builders, template library"
                width={sizes.side}
                glowColor="rgba(251,191,36,0.18)"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalsSection;
